// Modo live/test (`spec/17-frontend.md` §4.4). Só o seller tem toggle global; o admin
// filtra por tela. Persistido em `localStorage`, viaja em `X-Mode` e entra na query key.

import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, use, useCallback, useMemo, useState } from 'react';

export type Mode = 'live' | 'test';

export const MODE_STORAGE_KEY = 'lm.mode';

export function readStoredMode(): Mode {
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) === 'test' ? 'test' : 'live';
  } catch {
    return 'live';
  }
}

interface ModeContextValue {
  mode: Mode;
  setMode: (mode: Mode) => void;
}

const ModeContext = createContext<ModeContextValue | undefined>(undefined);

export function ModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(readStoredMode);
  const queryClient = useQueryClient();

  const setMode = useCallback(
    (next: Mode) => {
      try {
        window.localStorage.setItem(MODE_STORAGE_KEY, next);
      } catch {
        // Navegador sem storage: o modo vale só nesta aba.
      }
      setModeState(next);
      // Trocar o modo invalida o cache inteiro do seller (`spec/17` §4.4).
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);
  return <ModeContext value={value}>{children}</ModeContext>;
}

export function useMode(): ModeContextValue {
  const value = use(ModeContext);
  if (value === undefined) {
    throw new Error('useMode fora do ModeProvider');
  }
  return value;
}

/**
 * Versão que não explode fora do provider. Existe para o `ModeToggle` do `PageHeader`
 * (WEB-50): o cabeçalho do kit é renderizado por ~45 telas e por testes de unidade que
 * montam a tela sozinha, sem os providers do `__root`. Lá o toggle simplesmente não
 * aparece, em vez de derrubar o render.
 */
export function useModeOrNull(): ModeContextValue | null {
  return use(ModeContext) ?? null;
}
